"use client";

import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "../../lib/utils";
import { pickerTriggerClass } from "./trigger-recipes";

/**
 * Job A — CHOOSE (`docs/picker-taxonomy.md` §1.2, §3.1, §4.1).
 *
 * A taxonomy that classifies the record — status, priority, type, category,
 * visibility, mode, format, frequency — with twelve options or fewer. Past
 * that, or for anything naming a person / project / task / milestone /
 * permission role, the mechanism is `Picker`, not this.
 */

const Select = SelectPrimitive.Root;
const SelectGroup = SelectPrimitive.Group;
const SelectValue = SelectPrimitive.Value;

export interface SelectTriggerProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> {
  /**
   * Trigger geometry (§2.1). Shared with `Picker` — a `Select` trigger and a
   * `Picker` trigger in the same context are pixel-identical by construction.
   *
   * Width comes from the parent (`<div className="w-32">`), never from a
   * class on the trigger.
   */
  variant?: "field" | "inline" | "compact";
  /**
   * The caret is rendered here, not by the call site, so no call site can
   * hand-add a different one. `false` only for a trigger that is its own
   * domain glyph.
   */
  chevron?: boolean;
}

const SelectTrigger = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(
  (
    { className, children, variant = "field", chevron = true, ...props },
    ref,
  ) => (
    <SelectPrimitive.Trigger
      ref={ref}
      className={cn(
        pickerTriggerClass(variant),
        // The value slot recipe (§2.1: `flex min-w-0 items-center gap-1.5
        // truncate`), applied from here so it reaches both shapes of value
        // slot: Radix's generated `SelectValue` span, and a hand-written span
        // at a call site that needs its own clamp (`property-menus.tsx`).
        //
        // This must NOT set `display`. `line-clamp-1` — the shadcn default
        // that used to sit here — resolves to `display:-webkit-box` +
        // `-webkit-box-orient:vertical` at specificity (0,1,1), which beats
        // the `flex` on a call site's own value slot and collapsed
        // `<StatusDot/>{label}` back to "•Label" with the gap gone. That is
        // the exact bug `SelectItem`'s `ItemText` recipe below exists to fix,
        // reappearing on the closed trigger.
        "[&>span]:flex [&>span]:min-w-0 [&>span]:items-center [&>span]:gap-1.5 [&>span]:truncate",
        className,
      )}
      {...props}
    >
      {children}
      {chevron ? (
        <SelectPrimitive.Icon asChild>
          <ChevronDown
            className={cn(
              "text-text-tertiary",
              variant === "compact" ? "size-3" : "size-3.5",
            )}
          />
        </SelectPrimitive.Icon>
      ) : null}
    </SelectPrimitive.Trigger>
  ),
);
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectContent = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        // `max-h-104` (26rem), not `max-h-96`: the Job A ceiling is 12 options
        // shown at once (`PICKER_THRESHOLD`) = 12 × `h-control` (32px) + a group
        // eyebrow + `p-1` = 412px. At 384px the twelfth row scrolled.
        "relative z-50 max-h-104 min-w-32 overflow-hidden rounded-lg",
        // Overlay tier (§2): the panel is separated from what it covers by
        // elevation, so the popover shadow replaces the hairline entirely.
        "bg-elevated text-text shadow-popover",
        "data-[state=open]:animate-in data-[state=open]:fade-in-0",
        "data-[state=open]:slide-in-from-top-1",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
        "duration-120 ease-out-fast",
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className,
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          "p-1",
          position === "popper" &&
            "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectItem = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex h-control w-full cursor-default select-none items-center gap-2",
      "rounded-sm pl-2 pr-8 text-sm text-text outline-none",
      "transition-[background-color,color] duration-100 ease-out-fast",
      "data-[highlighted]:bg-hover data-[state=checked]:text-text",
      "data-disabled:pointer-events-none data-disabled:opacity-50",
      className,
    )}
    {...props}
  >
    <span className="absolute right-2 flex size-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="size-3.5 text-accent-text" />
      </SelectPrimitive.ItemIndicator>
    </span>
    {/* The row's `gap-2` only separates this text from the absolutely-positioned
      * check, so an item rendered as `<StatusDot/>{label}` collapses to
      * "•Label" with no space.
      *
      * The layout MUST go on an element inside `ItemText`, never on `ItemText`
      * itself: Radix destructures `className` and `style` off its props and
      * never applies them (react-select@2.2.6 SelectItemText), so styling it
      * directly fails silently. Radix portals `itemTextProps.children` into the
      * trigger's value node, so this wrapper travels with it and spaces the
      * closed trigger too. */}
    <SelectPrimitive.ItemText>
      <span className="flex min-w-0 items-center gap-1.5">{children}</span>
    </SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectLabel = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn(
      "px-2 py-1 text-xs font-semibold uppercase tracking-wide text-text-tertiary",
      className,
    )}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectSeparator = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    // Separates by space, not by a rule (§1, `divide-y` → gap): the element is
    // kept so no call site changes, but it paints nothing.
    className={cn("-mx-1 my-1 h-px", className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

function SelectScrollUpButton() {
  return (
    <SelectPrimitive.ScrollUpButton className="flex cursor-default items-center justify-center py-1 text-text-tertiary">
      <ChevronUp className="size-3.5" />
    </SelectPrimitive.ScrollUpButton>
  );
}

function SelectScrollDownButton() {
  return (
    <SelectPrimitive.ScrollDownButton className="flex cursor-default items-center justify-center py-1 text-text-tertiary">
      <ChevronDown className="size-3.5" />
    </SelectPrimitive.ScrollDownButton>
  );
}

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
